import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Radio, Lock, ShieldCheck } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      await login(email, password);
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      {/* Left: brand panel */}
      <div className="relative hidden lg:flex flex-col justify-between p-12 border-r border-border bg-secondary/40 overflow-hidden">
        <div className="absolute inset-0 opacity-[0.06]" style={{ backgroundImage: "radial-gradient(hsl(var(--primary)) 1px, transparent 1px)", backgroundSize: "22px 22px" }} />
        <div className="relative flex items-center gap-3">
          <div className="grid h-11 w-11 place-items-center rounded-xl bg-primary/15 border border-primary/30">
            <Radio className="h-6 w-6 text-primary" />
          </div>
          <div>
            <div className="font-heading font-bold text-lg">Casey's Cadence</div>
            <div className="text-xs text-muted-foreground">Booking Engine</div>
          </div>
        </div>
        <div className="relative space-y-5 max-w-md">
          <h1 className="font-heading text-3xl font-bold tracking-tight leading-tight">
            A quiet operator console for trusted speaking throughput.
          </h1>
          <p className="text-sm text-muted-foreground leading-relaxed">
            Discover CFPs and retreat leads from public sources, score fit with transparent reasoning,
            draft tailored outreach — and route everything through a hardened human approval workflow.
          </p>
          <div className="space-y-2.5 pt-2">
            {["No autonomous send or submission", "Full provenance & audit trail", "Conservative compliance posture"].map((t) => (
              <div key={t} className="flex items-center gap-2.5 text-sm text-muted-foreground">
                <ShieldCheck className="h-4 w-4 text-emerald-400" /> {t}
              </div>
            ))}
          </div>
        </div>
        <div className="relative text-xs text-muted-foreground">Single-operator access · hardened Phase 1</div>
      </div>

      {/* Right: form */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm animate-in-up">
          <div className="lg:hidden flex items-center gap-2.5 mb-8">
            <div className="grid h-10 w-10 place-items-center rounded-lg bg-primary/15 border border-primary/30">
              <Radio className="h-5 w-5 text-primary" />
            </div>
            <span className="font-heading font-bold">Casey's Cadence</span>
          </div>
          <h2 className="font-heading text-2xl font-bold tracking-tight">Operator sign-in</h2>
          <p className="text-sm text-muted-foreground mt-1 mb-7">Access the booking engine console.</p>

          <Button type="button" onClick={handleGoogle} variant="outline" data-testid="login-google-button"
            className="w-full h-11 gap-2.5 font-medium">
            <GoogleIcon /> Continue with Google
          </Button>

          <div className="flex items-center gap-3 my-6">
            <div className="h-px flex-1 bg-border" />
            <span className="text-[11px] uppercase tracking-wider text-muted-foreground">or email</span>
            <div className="h-px flex-1 bg-border" />
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                required data-testid="login-email-input" placeholder="you@avenuespeak.info" className="h-11" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                required data-testid="login-password-input" placeholder="••••••••" className="h-11" />
            </div>
            {error && <p className="text-sm text-rose-400" data-testid="login-error">{error}</p>}
            <p className="text-xs text-muted-foreground">Normally use Google? Click “Continue with Google” above. To use email sign-in, set an email password from the app after signing in once with Google.</p>
            <Button type="submit" disabled={busy} data-testid="login-submit-button" className="w-full h-11 gap-2">
              <Lock className="h-4 w-4" /> {busy ? "Signing in…" : "Sign in"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg className="h-4 w-4" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1Z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23Z"/><path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84Z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1A11 11 0 0 0 2.18 7.06l3.66 2.84C6.71 7.3 9.14 5.38 12 5.38Z"/></svg>
  );
}
